/**
 * @fileoverview Google Maps JavaScript API Loader (Places API v2)
 * @module googleMapsLoader
 *
 * Business Context:
 * - Loads the Google Maps JavaScript API on-demand using the importLibrary pattern
 * - Required for PlaceAutocompleteElement (Places API v2) integration
 *
 * Technical Context:
 * - Uses the official Google Maps bootstrap loader pattern (not @react-google-maps/api)
 * - Injects the Maps JS bootstrap script once and reuses across components
 * - importLibrary('places') returns the PlacesLibrary containing PlaceAutocompleteElement
 * - API key is read from VITE_GOOGLE_MAPS_API_KEY environment variable
 *
 * Migration Note:
 * - Replaces useLoadScript from @react-google-maps/api (legacy Places API)
 * - PlaceAutocompleteElement (Places API v2) is now in stable release (v3.64+, Feb 2026)
 */

let loaderPromise: Promise<void> | null = null;

/**
 * Injects the Google Maps JavaScript API bootstrap script exactly once.
 * Subsequent calls return the same promise.
 */
function injectBootstrap(apiKey: string): Promise<void> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Google Maps loader must run in a browser context'));
      return;
    }

    // Already loaded — check via duck typing to avoid strict null issues
    const g = window as unknown as { google?: { maps?: { importLibrary?: unknown } } };
    if (g.google?.maps?.importLibrary) {
      resolve();
      return;
    }

    const callbackName = '__gm_callback__';
    (window as unknown as Record<string, unknown>)[callbackName] = () => resolve();

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&loading=async&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Failed to load Google Maps JavaScript API'));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

/**
 * Loads the Google Maps Places library (v2).
 * Returns the PlacesLibrary which includes PlaceAutocompleteElement.
 */
export async function loadPlacesLibrary(apiKey: string): Promise<google.maps.PlacesLibrary> {
  await injectBootstrap(apiKey);
  return google.maps.importLibrary('places') as Promise<google.maps.PlacesLibrary>;
}
